import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import {
  excludeBreakpointVariables,
  generateInlineThemeVariables,
  getFontFamilies,
  loadFontFace,
  processFontFamily,
  generateTailwindVariables,
  generateVariantComponents,
  TAILWIND_CSS_CLASS,
} from './theme/themeHelpers';
import { Helmet } from 'react-helmet';
import { PageTreeNode } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import '@tailwindplus/elements';
import { FlexiblePagePortalProvider } from './FlexiblePagePortalContext';

interface PageProps {
  children: React.ReactNode;
  treeNode: PageTreeNode;
  editorClassName?: string;
  skipFontLoading?: boolean;
  staticAssetsBaseURL?: string | null;
}

type PageDivProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>;

export const Page = React.forwardRef<HTMLDivElement, PageProps>(
  (
    {
      children,
      treeNode,
      editorClassName,
      skipFontLoading,
      staticAssetsBaseURL,
      ...divProps
    }: PageProps & PageDivProps,
    ref,
  ) => {
    const { pageTitle, favicon, pageDescription, ...themeProps } = treeNode.data;

    const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
      if (skipFontLoading) return;

      const fontFaces = processFontFamily(getFontFamilies(treeNode.data), staticAssetsBaseURL);

      fontFaces.forEach((font) => {
        void loadFontFace(font.fontFamily, font.fontWeight, font.src, font.fontStyle);
      });
    }, [treeNode, skipFontLoading, staticAssetsBaseURL]);

    // Generate CSS variables, utilities, and variant components from flattened theme props
    const cssContent = useMemo(() => {
      const tailwindVars = generateTailwindVariables(themeProps);
      const scopedThemeVariables = excludeBreakpointVariables(tailwindVars);
      const inlineThemeVars = generateInlineThemeVariables(tailwindVars);
      const variantComponents = generateVariantComponents(themeProps);

      return {
        inlineThemeVars,
        scopedVariables: scopedThemeVariables,
        components: variantComponents,
      };
    }, [themeProps]);

    return (
      <>
        <Helmet>
          <style type="text/tailwindcss">{`
            @theme inline {
              ${cssContent.inlineThemeVars.join('\n              ')}
            }
            ${cssContent.components}

            /* Avoid tailwind normalizing font weights, use browser defaults for standard handling */
            b, strong {
              font-weight: 700;
            }
          `}</style>
        </Helmet>

        <main>
          <FlexiblePagePortalProvider container={portalContainer}>
            <div
              {...divProps}
              ref={ref}
              data-flexible-page-root
              style={{
                ...(divProps.style as React.CSSProperties),
                ...(cssContent.scopedVariables as React.CSSProperties),
              }}
              className={cx(TAILWIND_CSS_CLASS, editorClassName, divProps.className)}
            >
              {children}
              <div ref={setPortalContainer} data-flexible-page-modal-portal-root />
            </div>
          </FlexiblePagePortalProvider>
        </main>
      </>
    );
  },
);

Page.displayName = 'Page';
